import React, { useContext, useMemo, useRef, useState } from "react";
import Swal from 'sweetalert2';
import moment from "moment";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCalendar, faRoute } from "@fortawesome/free-solid-svg-icons";

import AddItm from '../addItm/AddItm';
import SearchInput from '../searchInput/SearchInput';
import StateChangeOPD from '../stateChangeOPD/StateChangeOPD';
import FilterScreen from '../filterScreen/FilterScreen';
import OPDInfo from '../opdInfo/OPDInfo';
import { Calendar } from '../calendar/Calendar';
import RutasOPD from '../rutasOPD/RutasOPD';
import opdContext from "../../context/opdContext/opdContext";
import activoContext from "../../context/activoContex/activoContex";
import { PRIORITYOUT } from '../../utils/priority';
import { getFiles } from '../../api/transactionDocuments';

import './OPDList.css';

const STATES_OPD = ['PENDIENTE', 'ENPROCESO', 'FINALIZADA'];

const OPDList = ({ opdList, handleClickAdd, handleClickEdit, usuario, showFilters, setShowFilters, filtros, aplicarFiltros, activeWorkSpace, setActiveWorkSpace, vistaActivo, setVistaActivo }) => {
    const [filter, setFilter] = useState('');
    const [selectedStateChange, setSelectedStateChange] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [pendientesVisibles, setPendientesVisibles] = useState({});
    const [excluirCompletas, setExcluirCompletas] = useState({});
    const [showCalendar, setShowCalendar] = useState(false);
    const [showRutas, setShowRutas] = useState(false);
    const monthsAlreadyLoaded = useRef([]);

    const { eliminarOPD, modificarOPD } = useContext(opdContext);
    const { activoList } = useContext(activoContext);

    // Activos habilitados para producción
    const activosProduccion = useMemo(() => {
        if (!activoList) return [];
        return activoList.filter(a => a.habilitadoProduccion === true);
    }, [activoList]);

    // Obtener IDs de activos asociados a una OPD (a través de tareas)
    const getActivosDeOPD = (opd) => {
        if (!opd.tareas) return [];
        const activoIds = new Set();
        opd.tareas.forEach(tarea => {
            if (tarea.activoIDRef) activoIds.add(tarea.activoIDRef);
        });
        return Array.from(activoIds);
    };

    // Obtener tareas de un activo específico dentro de una OPD (con índice original)
    const getTareasDelActivo = (opd, activoId) => {
        if (!opd.tareas) return [];
        return opd.tareas
            .map((t, idx) => ({ ...t, _originalIndex: idx }))
            .filter(t => t.activoIDRef === activoId);
    };

    // Obtener el máximo porcentaje de cumplimiento entre las tareas de un activo
    const getMaxPorcentajeActivo = (opd, activoId) => {
        const tareas = getTareasDelActivo(opd, activoId);
        if (tareas.length === 0) return 0;
        return Math.max(...tareas.map(t => parseFloat(t.porcentajeCumplimiento) || 0));
    };

    // Sumar cargaTiempo de todas las tareas de un activo
    const getCargaTiempoActivo = (opd, activoId) => {
        const tareas = getTareasDelActivo(opd, activoId);
        return tareas.reduce((sum, t) => sum + (parseFloat(t.cargaTiempo) || 0), 0);
    };

    // Obtener cantidades de los templates asociados a las tareas de un activo
    const getCantidadesTemplateActivo = (opd, activoId) => {
        const tareas = getTareasDelActivo(opd, activoId);
        const templateIds = new Set(tareas.map(t => t.fromTemplate).filter(Boolean));
        const result = { unidades: 0, metros: 0, kilos: 0 };
        if (!opd.templatesProduccion) return result;
        opd.templatesProduccion.forEach(template => {
            if (templateIds.has(template.value)) {
                result.unidades += parseFloat(template.unidades) || 0;
                result.metros += parseFloat(template.metros) || 0;
                result.kilos += parseFloat(template.kilos) || 0;
            }
        });
        return result;
    };

    // Toggle visibilidad de OPDs pendientes por activo
    const togglePendientesVisibles = (activoId) => {
        setPendientesVisibles(prev => ({
            ...prev,
            [activoId]: !prev[activoId]
        }));
    };

    const handleSearchInput = (e) => {
        setFilter(e);
    };

    const handleWorkSpaceTodos = () => {
        setActiveWorkSpace({ todos: 1, abiertas: 0, pendientes: 0, enproceso: 0, finalizadas: 0 });
        if (filtros && aplicarFiltros) {
            const filterEstado = { ...filtros.filtrosEstado, state: STATES_OPD };
            aplicarFiltros({ filterEstado, filterRango: filtros.filtrosRango });
        }
    };

    const handleWorkSpaceAbiertas = () => {
        setActiveWorkSpace({ todos: 0, abiertas: 1, pendientes: 0, enproceso: 0, finalizadas: 0 });
        if (filtros && aplicarFiltros) {
            const filterEstado = { ...filtros.filtrosEstado, state: ['PENDIENTE', 'ENPROCESO'] };
            aplicarFiltros({ filterEstado, filterRango: filtros.filtrosRango });
        }
    };

    const handleWorkSpacePendientes = () => {
        setActiveWorkSpace({ todos: 0, abiertas: 0, pendientes: 1, enproceso: 0, finalizadas: 0 });
        if (filtros && aplicarFiltros) {
            const filterEstado = { ...filtros.filtrosEstado, state: ['PENDIENTE'] };
            aplicarFiltros({ filterEstado, filterRango: filtros.filtrosRango });
        }
    };

    const handleWorkSpaceEnProceso = () => {
        setActiveWorkSpace({ todos: 0, abiertas: 0, pendientes: 0, enproceso: 1, finalizadas: 0 });
        if (filtros && aplicarFiltros) {
            const filterEstado = { ...filtros.filtrosEstado, state: ['ENPROCESO'] };
            aplicarFiltros({ filterEstado, filterRango: filtros.filtrosRango });
        }
    };

    const handleWorkSpaceFinalizadas = () => {
        setActiveWorkSpace({ todos: 0, abiertas: 0, pendientes: 0, enproceso: 0, finalizadas: 1 });
        if (filtros && aplicarFiltros) {
            const filterEstado = { ...filtros.filtrosEstado, state: ['FINALIZADA'] };
            aplicarFiltros({ filterEstado, filterRango: filtros.filtrosRango });
        }
    };

    const handleChangeState = (opd) => {
        setSelectedStateChange(opd);
    };

    const handleCloseStateChange = () => {
        setSelectedStateChange(null);
    };

    const handleDetails = async (opd) => {
        let files = [];
        try {
            const resp = await getFiles(opd.tDocCodFor, opd.tDocNroFor);
            files = resp.data.data || [];
        } catch (error) {
            console.log("Error al cargar archivos de la OPD", error);
        }
        setSelectedDetail({ item: opd, files });
    };

    const handleUpdateTareaPorcentaje = async (opd, tareaIndex, nuevoPorcentaje) => {
        const porcentajeNum = Math.min(100, Math.max(0, parseFloat(nuevoPorcentaje) || 0));

        // Construir array completo de tareas normalizado para el backend
        const tareasActualizadas = (opd.tareas || []).map((t, idx) => {
            const porcentaje = idx === tareaIndex ? porcentajeNum : (parseFloat(t.porcentajeCumplimiento) || 0);
            const done = porcentaje >= 100;
            return {
                value: t.codTarea || t.value || '',
                label: t.name || t.label || '',
                name: t.name || t.label || '',
                id: t.id || '',
                taskDescrp: t.taskDescrp || '',
                done,
                activo: t.codActivo ? {
                    id: t.activoIDRef || '',
                    value: t.codActivo,
                    label: t.activoNombre || t.codActivo
                } : t.activo || null,
                cargaTiempo: t.cargaTiempo || 0,
                fromTemplate: t.fromTemplate || '',
                unidadMedida: t.unidadMedida || '',
                horasCarga: t.horasCarga || '',
                porcentajeCumplimiento: porcentaje.toString()
            };
        });

        try {
            const updates = {};
            // Si la OPD está PENDIENTE y alguna tarea tiene porcentaje > 0, pasar a ENPROCESO
            if (opd.state === 'PENDIENTE' && tareasActualizadas.some(t => parseFloat(t.porcentajeCumplimiento) > 0)) {
                updates.state = 'ENPROCESO';
            }
            await modificarOPD(opd._id, updates, [], undefined, tareasActualizadas);
        } catch (error) {
            console.error('Error al actualizar porcentaje:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo actualizar el porcentaje'
            });
        }
    };

    const handleEditarCalendar = async (updates, _products, _imputaciones, _tareas, _fromAction) => {
        try {
            await modificarOPD(updates._id, { deliveryDate: updates.deliveryDateToUpdate }, []);
        } catch (error) {
            console.error('Error al actualizar fecha de entrega:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo actualizar la fecha de entrega' });
        }
    };

    const handleDelete = async (opd) => {
        Swal.fire({
            title: `Eliminar OPD ${opd.tDocNroFor}?`,
            showDenyButton: true,
            confirmButtonText: 'Si, estoy seguro',
            confirmButtonColor: '#ff0000',
            denyButtonColor: '#3d4d87',
            denyButtonText: 'No',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await eliminarOPD(opd.tDocNroFor, opd._id, 'delete');
                    Swal.fire({
                        title: 'OPD Eliminada!',
                        icon: 'success'
                    });
                } catch (error) {
                    Swal.fire({
                        title: 'Error',
                        text: 'No se pudo eliminar la OPD',
                        icon: 'error'
                    });
                }
            }
        });
    };

    // Filtrado local por búsqueda (los filtros avanzados y workspace ya vienen aplicados desde OPD.js)
    const opdFiltered = useMemo(() => {
        if (!opdList) return [];
        return opdList.filter(opd => {
            // Filtro por búsqueda
            if (filter === '') return true;
            const filterLower = filter.toLowerCase();
            const nroForString = `OPD - ${opd.tDocNroFor}`.toLowerCase();
            return (
                nroForString.includes(filterLower) ||
                opd.title?.toLowerCase().includes(filterLower) ||
                opd.tDocNroFor?.toString().includes(filterLower)
            );
        });
    }, [opdList, filter]);

    const getStateStyle = (state) => {
        switch (state) {
            case 'ENPROCESO':
                return { backgroundColor: 'rgb(228, 148, 0)', color: 'white' };
            case 'FINALIZADA':
                return { backgroundColor: 'greenyellow', color: 'black' };
            default:
                return { backgroundColor: 'rgb(198, 198, 198)', color: 'black' };
        }
    };

    const getStateLabel = (state) => {
        switch (state) {
            case 'PENDIENTE':
                return 'PENDIENTE';
            case 'ENPROCESO':
                return 'EN PROCESO';
            case 'FINALIZADA':
                return 'FINALIZADA';
            default:
                return state || 'PENDIENTE';
        }
    };

    if (opdList === null) return <h3 className="warningStatusMsg">Cargando <span>.</span><span>.</span><span>.</span></h3>;

    return (
        <div className="opdListView">
            <div className="utilitiesDivOPD">
                <AddItm titulo={'OPD'} anadiritm={handleClickAdd} />
                <button className="calendarBtnOPD rutasBtnOPD" onClick={() => setShowRutas(true)}>
                    RUTAS <FontAwesomeIcon icon={faRoute} />
                </button>
                <button className="calendarBtnOPD" onClick={() => setShowCalendar(!showCalendar)}>
                    CALENDARIO <FontAwesomeIcon icon={faCalendar} />
                </button>
                <button
                    className={`vistaActivoBtn ${vistaActivo ? 'active' : ''}`}
                    onClick={() => setVistaActivo(!vistaActivo)}
                >
                    {vistaActivo ? <><span>CAMBIAR A</span><br /><span>VISTA NORMAL</span></> : <><span>CAMBIAR A</span><br /><span>VISTA POR ACTIVO</span></>}
                </button>
                <FilterScreen verFiltro={setShowFilters} />
                <SearchInput buscar={handleSearchInput} filter={filter} />
            </div>
            <div className="navbar-opdList">
                <div
                    className={activeWorkSpace.abiertas ? "navbar-container-opdList-active" : "navbar-container-opdList"}
                    onClick={handleWorkSpaceAbiertas}
                >
                    <div className="navbar-item-opdList">
                        <div className="navbar-item-label-xx">ABIERTAS</div>
                    </div>
                </div>
                <div
                    className={activeWorkSpace.pendientes ? "navbar-container-opdList-active" : "navbar-container-opdList"}
                    onClick={handleWorkSpacePendientes}
                >
                    <div className="navbar-item-opdList">
                        <div className="navbar-item-label-xx">PENDIENTES</div>
                    </div>
                </div>
                <div
                    className={activeWorkSpace.enproceso ? "navbar-container-opdList-active" : "navbar-container-opdList"}
                    onClick={handleWorkSpaceEnProceso}
                >
                    <div className="navbar-item-opdList">
                        <div className="navbar-item-label-xx">EN PROCESO</div>
                    </div>
                </div>
                <div
                    className={activeWorkSpace.finalizadas ? "navbar-container-opdList-active" : "navbar-container-opdList"}
                    onClick={handleWorkSpaceFinalizadas}
                >
                    <div className="navbar-item-opdList">
                        <div className="navbar-item-label-xx">FINALIZADAS</div>
                    </div>
                </div>
                <div
                    className={activeWorkSpace.todos ? "navbar-container-opdList-active" : "navbar-container-opdList"}
                    onClick={handleWorkSpaceTodos}
                >
                    <div className="navbar-item-opdList">
                        <div className="navbar-item-label-xx">TODOS</div>
                    </div>
                </div>
            </div>

            {vistaActivo ? (
                /* Vista por Activo */
                <div className="container-opd-activos">
                    {activosProduccion.length === 0 ? (
                        <h3 className="warningStatusMsg">No hay activos habilitados para producción</h3>
                    ) : (
                        <div className="opdActivosGrid">
                            {activosProduccion.map(activo => {
                                const opdsDelActivo = opdFiltered.filter(opd =>
                                    getActivosDeOPD(opd).includes(activo._id)
                                );
                                const sortByDeliveryAndNroFor = (a, b) => {
                                    const dateA = a.deliveryDate ? new Date(a.deliveryDate) : new Date('9999-12-31');
                                    const dateB = b.deliveryDate ? new Date(b.deliveryDate) : new Date('9999-12-31');
                                    if (dateA.getTime() !== dateB.getTime()) {
                                        return dateA - dateB;
                                    }
                                    return (a.tDocNroFor || 0) - (b.tDocNroFor || 0);
                                };

                                const opdsSinIniciar = opdsDelActivo
                                    .filter(opd => getMaxPorcentajeActivo(opd, activo._id) === 0)
                                    .sort(sortByDeliveryAndNroFor);
                                const opdsEnProgreso = opdsDelActivo
                                    .filter(opd => getMaxPorcentajeActivo(opd, activo._id) > 0)
                                    .sort(sortByDeliveryAndNroFor);

                                const excluir100 = excluirCompletas[activo._id] || false;

                                const renderOpdCard = (opd) => {
                                    const maxPorcentaje = getMaxPorcentajeActivo(opd, activo._id);
                                    const tareasActivo = getTareasDelActivo(opd, activo._id);
                                    const tareasVisibles = excluir100 ? tareasActivo.filter(t => (parseFloat(t.porcentajeCumplimiento) || 0) < 100) : tareasActivo;
                                    if (excluir100 && tareasVisibles.length === 0) return null;

                                    // Buscar tareas en otros activos donde esta OPD tiene progreso (>0%)
                                    let otrosTareasEnProgreso = [];
                                    if (maxPorcentaje === 0) {
                                        const activosDeOPD = getActivosDeOPD(opd);
                                        activosDeOPD.forEach(activoIdOtro => {
                                            if (activoIdOtro !== activo._id) {
                                                const tareasOtro = getTareasDelActivo(opd, activoIdOtro);
                                                const activoInfo = activosProduccion.find(a => a._id === activoIdOtro);
                                                if (activoInfo) {
                                                    tareasOtro.forEach(t => {
                                                        const pct = parseFloat(t.porcentajeCumplimiento) || 0;
                                                        if (pct > 0) {
                                                            otrosTareasEnProgreso.push({
                                                                nombre: activoInfo.codActivo || activoInfo.name,
                                                                porcentaje: pct,
                                                                codTarea: t.codTarea || t.value || ''
                                                            });
                                                        }
                                                    });
                                                }
                                            }
                                        });
                                    }

                                    return (
                                        <div key={opd._id} className="opdCardMini">
                                            <div className="opdCardMiniHeader">
                                                <span className="opdCardMiniClave">OPD-{opd.tDocNroFor} - {opd.title || '-'}</span>
                                                <span className={`opdCardMiniPriority ${opd.priority === 2 ? 'urgente' : opd.priority === 1 ? 'alta' : 'media'}`}>
                                                    {PRIORITYOUT[opd.priority ?? 0]}
                                                </span>
                                            </div>
                                            {otrosTareasEnProgreso.length > 0 && (
                                                <div className="opdCardMiniUbicacion">
                                                    {otrosTareasEnProgreso.map((a, idx) => (
                                                        <span key={idx}>Actualmente en {a.nombre} al {a.porcentaje}% ({a.codTarea})</span>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="opdCardMiniStateRow">
                                                <div className="opdCardMiniState" style={getStateStyle(opd.state)}>
                                                    {getStateLabel(opd.state)}
                                                </div>
                                                <div className="opdCardMiniEntrega">
                                                    Entrega: {opd.deliveryDate ? moment(opd.deliveryDate).format('DD-MM-YYYY') : '-'}
                                                </div>
                                            </div>
                                            <div className="opdCardMiniTareasList">
                                                {tareasVisibles.map((tarea) => {
                                                    const pct = parseFloat(tarea.porcentajeCumplimiento) || 0;
                                                    const horas = parseFloat(tarea.cargaTiempo) || 0;
                                                    return (
                                                        <div key={tarea._originalIndex} className="opdCardMiniTareaRow">
                                                            <span className="tareaCardNameLabel" title={tarea.name || tarea.label}>{tarea.name || tarea.label}</span>
                                                            <div className={`opdCardMiniPorcentaje ${pct === 0 ? 'porcentaje-cero' : pct >= 100 ? 'porcentaje-completo' : 'porcentaje-parcial'}`}>
                                                                {opd.state !== 'FINALIZADA' ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max="100"
                                                                        defaultValue={pct}
                                                                        className={`porcentajeInput ${pct === 0 ? 'input-cero' : pct >= 100 ? 'input-completo' : 'input-parcial'}`}
                                                                        onChange={(e) => {
                                                                            let val = parseInt(e.target.value);
                                                                            if (val > 100) e.target.value = 100;
                                                                            if (val < 0) e.target.value = 0;
                                                                        }}
                                                                        onBlur={(e) => handleUpdateTareaPorcentaje(opd, tarea._originalIndex, e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.target.blur();
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span>{pct}</span>
                                                                )}
                                                                %
                                                            </div>
                                                            <span className="tareaCardHorasLabel">{horas.toFixed(1)}hs</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="opdCardMiniActions">
                                                {opd.state !== 'FINALIZADA' && (
                                                    <button onClick={() => handleChangeState(opd)}>Modificar Estado</button>
                                                )}
                                                {opd.state !== 'FINALIZADA' && (
                                                    <button onClick={() => handleClickEdit(opd)}>Editar</button>
                                                )}
                                                <button onClick={() => handleDetails(opd)}>Detalles</button>
                                            </div>
                                        </div>
                                    );
                                };

                                // Calcular totales de OPDs sin iniciar
                                const totalesPendientes = opdsSinIniciar.reduce((acc, opd) => {
                                    const cantidades = getCantidadesTemplateActivo(opd, activo._id);
                                    return {
                                        horas: acc.horas + getCargaTiempoActivo(opd, activo._id),
                                        metros: acc.metros + cantidades.metros,
                                        kilos: acc.kilos + cantidades.kilos
                                    };
                                }, { horas: 0, metros: 0, kilos: 0 });
                                const mostrarPendientes = pendientesVisibles[activo._id] !== false;

                                // Calcular horas restantes de OPDs en progreso
                                const horasRestantesEnProgreso = opdsEnProgreso.reduce((acc, opd) => {
                                    const tareasActivo = getTareasDelActivo(opd, activo._id);
                                    return acc + tareasActivo.reduce((sum, t) => {
                                        const carga = parseFloat(t.cargaTiempo) || 0;
                                        const pct = parseFloat(t.porcentajeCumplimiento) || 0;
                                        return sum + carga * (1 - pct / 100);
                                    }, 0);
                                }, 0);

                                return (
                                    <div key={activo._id} className="opdActivoColumn">
                                        {/* Resumen de OPDs pendientes */}
                                        {opdsSinIniciar.length > 0 && (
                                            <div className="opdActivoPendientesResumen">
                                                <span className="pendientesInfo">
                                                    {totalesPendientes.horas.toFixed(2)} hs | {totalesPendientes.metros.toFixed(2)} mts | {totalesPendientes.kilos.toFixed(2)} kg pendientes ({opdsSinIniciar.length} OPDs)
                                                </span>
                                                <FontAwesomeIcon
                                                    icon={mostrarPendientes ? faEye : faEyeSlash}
                                                    className="pendientesToggle"
                                                    onClick={() => togglePendientesVisibles(activo._id)}
                                                />
                                            </div>
                                        )}
                                        {/* OPDs con 0% - Arriba del título */}
                                        {opdsSinIniciar.length > 0 && mostrarPendientes && (
                                            <div className="opdActivoColumnPendientes">
                                                {opdsSinIniciar.map(renderOpdCard)}
                                            </div>
                                        )}
                                        <div className="opdActivoColumnHeader">
                                            {opdsEnProgreso.length > 0 && (
                                                <span className="activoHorasRestantes">
                                                    {horasRestantesEnProgreso.toFixed(2)} hs aprox. para finalizar OPDs iniciadas
                                                </span>
                                            )}
                                            <span className="activoNombre">{activo.name}</span>
                                            {activo.imageURL && (
                                                <img
                                                    src={activo.imageURL}
                                                    alt={activo.name}
                                                    className="activoImagen"
                                                />
                                            )}
                                            <label className="excluirCompletasLabel">
                                                <input
                                                    type="checkbox"
                                                    checked={excluir100}
                                                    onChange={() => setExcluirCompletas(prev => ({ ...prev, [activo._id]: !prev[activo._id] }))}
                                                />
                                                Excluir tareas al 100%
                                            </label>
                                        </div>
                                        <div className="opdActivoColumnBody">
                                            {opdsEnProgreso.length === 0 && opdsSinIniciar.length === 0 ? (
                                                <div className="noOpdsMsg">Sin OPDs</div>
                                            ) : opdsEnProgreso.length === 0 ? (
                                                <div className="noOpdsMsg">Sin OPDs iniciadas</div>
                                            ) : (
                                                opdsEnProgreso.map(renderOpdCard)
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                /* Vista Lista */
                opdFiltered.length === 0 ? (
                    <h3 className="warningStatusMsg">No hay Órdenes de Producción cargadas</h3>
                ) : (
                    <div className="container-opd-list">
                        <div className="opdList">
                            {opdFiltered.map((opd, i) => (
                                <div key={opd._id} className="container-r-list-item">
                                    <div className="container-r-list-item-header">
                                        <div className="r-list-item-header-clave">OPD - {opd.tDocNroFor}</div>
                                        <div className="r-list-item-header-title">{opd.title || '-'}</div>
                                        <div className="margin-right-r">
                                            <div className={opd.priority === 0 ? 'r-list-item-header-priority-media' : opd.priority === 1 ? 'r-list-item-header-priority-alta' : 'r-list-item-header-priority-urgente'}>
                                                {PRIORITYOUT[opd.priority ?? 0]}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="container-r-list-item-details">
                                        <div
                                            className="r-list-item-details-clave"
                                            style={getStateStyle(opd.state)}
                                        >
                                            {getStateLabel(opd.state)}
                                        </div>
                                        <div className="r-list-item-details-title">
                                            Creada el: {moment(opd.fecAlt).format('DD-MM-YYYY HH:mm')}
                                        </div>
                                        <div className="r-list-item-details-title">
                                            Usuario: {opd.userName || '-'}
                                        </div>
                                        <div className="r-list-item-details-title">
                                            Fecha de Movimiento: {opd.fecMov ? moment(opd.fecMov).format('DD-MM-YYYY') : '-'}
                                        </div>
                                        <div className="r-list-item-details-title">
                                            Fecha de Entrega: {opd.deliveryDate ? moment(opd.deliveryDate).format('DD-MM-YYYY') : '-'}
                                        </div>
                                    </div>
                                    <div className="container-r-list-item-btn">
                                        {opd.state !== 'FINALIZADA' && (
                                            <div
                                                className="r-list-item-btn-A"
                                                onClick={() => handleChangeState(opd)}
                                            >
                                                Modificar Estado
                                            </div>
                                        )}
                                        {opd.state !== 'FINALIZADA' && (
                                            <div
                                                className="r-list-item-btn-A"
                                                onClick={() => handleClickEdit(opd)}
                                            >
                                                Editar
                                            </div>
                                        )}
                                        {opd.state !== 'FINALIZADA' && (
                                            <div
                                                className="r-list-item-btn-A"
                                                id="deleteODT"
                                                onClick={() => handleDelete(opd)}
                                            >
                                                Eliminar
                                            </div>
                                        )}
                                        <div
                                            className="r-list-item-btn-A"
                                            onClick={() => handleDetails(opd)}
                                        >
                                            Detalles
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {selectedStateChange && (
                <div className="modalContainer">
                    <StateChangeOPD
                        opd={selectedStateChange}
                        guardarEstado={modificarOPD}
                        cerrar={handleCloseStateChange}
                    />
                </div>
            )}

            {selectedDetail && (
                <div className="modalContainer">
                    <OPDInfo
                        item={selectedDetail.item}
                        files={selectedDetail.files}
                        cerrar={() => setSelectedDetail(null)}
                    />
                </div>
            )}

            {showCalendar && (
                <div className="modalContainer">
                    <Calendar
                        data={opdFiltered.map(opd => ({ ...opd, codActivo: opd.title || '' }))}
                        getAndMergeTDocsByFecAlt={() => {}}
                        setShowCalendar={setShowCalendar}
                        handlerDetails={handleDetails}
                        monthsAlreadyLoaded={monthsAlreadyLoaded}
                        editar={handleEditarCalendar}
                    />
                </div>
            )}

            {showRutas && (
                <RutasOPD
                    opdList={opdFiltered}
                    cerrar={() => setShowRutas(false)}
                />
            )}
        </div>
    );
};

export default OPDList;
